"use client";

import React, { useEffect, useState } from "react";
import { Plus, MessageSquare, Trash2, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  ButtonPrimary,
  ButtonOutline,
} from "@/components/common/StandardButton";
import StandardCard from "@/components/common/StandardCard";
import { CreateCustomerNoteDto } from "@/types/models/customer";
import { useForm, SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format } from "date-fns";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "@/store/store";
import {
  fetchCustomerNotesRequest,
  createCustomerNoteRequest,
  deleteCustomerNoteRequest,
  resetNoteFormState,
} from "@/store/slices/customer.slice";

interface CustomerNotesCardProps {
  customerId: string;
}

// Form validation schema
const noteSchema = z.object({
  note: z.string().min(1, "Note content is required"),
});

const CustomerNotesCard: React.FC<CustomerNotesCardProps> = ({
  customerId,
}) => {
  const dispatch = useDispatch();
  const [showAddForm, setShowAddForm] = useState(false);

  // Redux state
  const { notes, notesLoading, notesError, noteFormState } = useSelector(
    (state: RootState) => state.customers,
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateCustomerNoteDto>({
    resolver: zodResolver(noteSchema),
    mode: "onSubmit",
  });

  // Fetch notes on mount
  useEffect(() => {
    if (customerId) {
      dispatch(fetchCustomerNotesRequest({ customerId }));
    }
  }, [customerId, dispatch]);

  // Handle form success
  useEffect(() => {
    if (noteFormState.success) {
      reset();
      setShowAddForm(false);
      dispatch(resetNoteFormState());
    }
  }, [noteFormState.success, reset, dispatch]);

  const onSubmit: SubmitHandler<CreateCustomerNoteDto> = (data) => {
    dispatch(createCustomerNoteRequest({ customerId, note: data }));
  };

  const handleDeleteNote = (noteId: string) => {
    if (confirm("Are you sure you want to delete this note?")) {
      dispatch(deleteCustomerNoteRequest({ customerId, noteId }));
    }
  };

  const handleCancelAdd = () => {
    setShowAddForm(false);
    reset();
    dispatch(resetNoteFormState());
  };

  return (
    <StandardCard>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-primary" />
            <h4 className="text-sm font-medium text-primary uppercase tracking-wider">
              Customer Notes
            </h4>
          </div>
          {!showAddForm && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAddForm(true)}
              className="h-7 px-2 text-xs"
            >
              <Plus className="h-3 w-3 mr-1" />
              Add Note
            </Button>
          )}
        </div>

        {/* Add Note Form */}
        {showAddForm && (
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-3 p-3 bg-muted/30 rounded-lg border"
          >
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Note <span className="text-destructive">*</span>
              </label>
              <div className="bg-background border border-border rounded-xl overflow-hidden">
                <textarea
                  className="w-full px-4 py-2 bg-transparent placeholder-gray-500 outline-none min-h-[80px] resize-vertical"
                  placeholder="Enter your note here..."
                  {...register("note")}
                />
              </div>
              {errors.note && (
                <p className="text-sm text-destructive mt-1">
                  {errors.note.message}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2 justify-end">
              <ButtonOutline
                type="button"
                size="sm"
                onClick={handleCancelAdd}
                disabled={noteFormState.loading}
              >
                Cancel
              </ButtonOutline>
              <ButtonPrimary
                type="submit"
                size="sm"
                disabled={noteFormState.loading}
              >
                {noteFormState.loading ? "Adding..." : "Add Note"}
              </ButtonPrimary>
            </div>
            {noteFormState.error && (
              <div className="text-sm text-destructive">
                {noteFormState.error.message}
              </div>
            )}
          </form>
        )}

        {/* Notes List */}
        <div className="space-y-3">
          {notesLoading ? (
            <div className="space-y-3">
              {[1, 2].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-4 bg-muted rounded w-32 mb-2"></div>
                  <div className="h-3 bg-muted rounded w-full mb-1"></div>
                  <div className="h-3 bg-muted rounded w-20"></div>
                </div>
              ))}
            </div>
          ) : notesError ? (
            <div className="text-center text-destructive text-sm">
              {notesError.message}
            </div>
          ) : notes.length === 0 ? (
            <div className="text-center text-muted-foreground text-sm py-4">
              No notes found for this customer
            </div>
          ) : (
            notes.map((note) => (
              <div
                key={note.id}
                className="p-3 bg-muted/20 rounded-lg border space-y-2"
              >
                <div className="flex items-start justify-between">
                  <p className="text-sm text-foreground leading-relaxed flex-1">
                    {note.note || note.description}
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteNote(note.id)}
                    className="h-6 w-6 p-0 text-destructive hover:text-destructive hover:bg-destructive/10 ml-2 flex-shrink-0"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Calendar className="h-3 w-3" />
                  {format(
                    new Date(note.created_at),
                    "MMM dd, yyyy 'at' h:mm a",
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </StandardCard>
  );
};

export default CustomerNotesCard;
